import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Phone } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { Reveal } from "../components/reveal";
import { LazyMount } from "../components/lazy-mount";
import { BeforeAfterCard, type BeforeAfterItem } from "../components/before-after-card";
import { InlineQuoteSection } from "@/components/inline-quote-section";
import { InternalLinkRow, POPULAR_SERVICE_LINKS, CORE_CITY_LINKS } from "@/components/internal-links";
import { QuickLeadForm } from "@/components/quick-lead-form";

import houseBefore from "../assets/before-after/house-wash-before.webp.asset.json";
import houseAfter from "../assets/before-after/house-wash-after.webp.asset.json";
import { roofWashBeforeOpt as roofBefore } from "../assets/opt";
import { roofWashAfterOpt as roofAfter } from "../assets/opt";
import concreteBefore from "../assets/before-after/concrete-before.png.asset.json";
import concreteAfter from "../assets/before-after/concrete-after.png.asset.json";
import paverBefore from "../assets/before-after/paver-before.png.asset.json";
import paverAfter from "../assets/before-after/paver-after.png.asset.json";
import deckBefore from "../assets/before-after/deck-before.png.asset.json";
import deckAfter from "../assets/before-after/deck-after.png.asset.json";
import gutterBefore from "../assets/before-after/gutter-before.png.asset.json";
import gutterAfter from "../assets/before-after/gutter-after.png.asset.json";
import gutterInsideBefore from "../assets/before-after/gutter-inside-before.webp.asset.json";
import gutterInsideAfter from "../assets/before-after/gutter-inside-after.webp.asset.json";
import tileBefore from "../assets/before-after/house-wash-tile-before.png.asset.json";
import tileAfter from "../assets/before-after/house-wash-tile-after.png.asset.json";

const GalleryVideoSlider = lazy(() =>
  import("../components/gallery-video-slider").then((m) => ({ default: m.GalleryVideoSlider })),
);

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Before & After Gallery | Pressure Washing Albany NY | Capital Pro";
const DESC =
  "Real before-and-after pressure washing photos from Albany, Troy, Schenectady and Saratoga Springs — house washing, roof washing, concrete, pavers, decks and gutters.";

const ITEMS: BeforeAfterItem[] = [
  {
    title: "Vinyl Siding House Wash",
    location: "Albany, NY",
    service: "House Washing",
    href: "/house-washing",
    before: houseBefore.url,
    after: houseAfter.url,
    alt: "Green algae removed from vinyl siding on an Albany home",
  },
  {
    title: "Black Streak Roof Wash",
    location: "Troy, NY",
    service: "Roof Washing",
    href: "/roof-washing",
    before: roofBefore.url,
    after: roofAfter.url,
    alt: "Black streaks soft washed off an asphalt shingle roof",
  },
  {
    title: "Driveway & Walkway Cleaning",
    location: "Colonie, NY",
    service: "Concrete Cleaning & Sealing",
    href: "/concrete-cleaning-sealing",
    before: concreteBefore.url,
    after: concreteAfter.url,
    alt: "Concrete driveway surface cleaned of dirt and organic staining",
  },
  {
    title: "Paver Patio Restoration",
    location: "Clifton Park, NY",
    service: "Paver Cleaning & Sealing",
    href: "/paver-cleaning-sealing",
    before: paverBefore.url,
    after: paverAfter.url,
    alt: "Paver patio cleaned, re-sanded and sealed",
  },
  {
    title: "Wood Deck Cleaning",
    location: "Schenectady, NY",
    service: "Deck & Patio Cleaning",
    href: "/deck-patio-cleaning",
    before: deckBefore.url,
    after: deckAfter.url,
    alt: "Wood deck boards cleaned of graying and mildew",
  },
  {
    title: "Gutter Face Cleaning",
    location: "Saratoga Springs, NY",
    service: "Gutter Cleaning",
    href: "/gutter-cleaning",
    before: gutterBefore.url,
    after: gutterAfter.url,
    alt: "Exterior gutter face washed clean of grime",
  },
  {
    title: "Clogged Gutter Clear-Out",
    location: "Niskayuna, NY",
    service: "Gutter Cleaning",
    href: "/gutter-cleaning",
    before: gutterInsideBefore.url,
    after: gutterInsideAfter.url,
    alt: "Gutter trough cleared of packed leaves and debris",
  },
  {
    title: "Full Exterior Soft Wash",
    location: "Delmar, NY",
    service: "Soft Washing",
    href: "/soft-washing",
    before: tileBefore.url,
    after: tileAfter.url,
    alt: "Whole-home exterior soft wash before and after",
  },
];

export const Route = createFileRoute("/gallery")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/gallery` },
      { property: "og:image", content: `${SITE}${houseAfter.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: `${SITE}${houseAfter.url}` },
    ],
    links: [{ rel: "canonical", href: `${SITE}/gallery` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "Capital Pro Pressure Washing Before & After Gallery",
          url: `${SITE}/gallery`,
          description: DESC,
          image: ITEMS.map((i) => `${SITE}${i.after}`),
        }),
      },
    ],
  }),
  component: GalleryPage,
});

const DISPLAY = { fontFamily: "var(--font-display)", fontWeight: 700 } as const;
const H2 = "text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight";

function GalleryPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Gallery" }]} />

      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Real Capital Region Jobs
          </p>
          <h1 className="text-3xl md:text-5xl uppercase leading-tight" style={DISPLAY}>
            Before &amp; After Pressure Washing Gallery
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Every photo below is our own work on homes around Albany, Troy, Schenectady, Colonie,
            Clifton Park and Saratoga Springs. Drag the slider on any photo to see the difference
            for yourself.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              Get My Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-8 md:py-10">
        <div className="mx-auto max-w-[760px] px-4">
          <QuickLeadForm source="gallery-top" />
        </div>
      </section>

      <section className="bg-[var(--brand-light-gray)] py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <h2 className={`${H2} text-center mb-8`} style={DISPLAY}>Drag To Compare</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ITEMS.map((item, i) => (
              <Reveal key={item.title} delayMs={(i % 3) * 60}>
                <BeforeAfterCard item={item} />
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-center text-[var(--brand-charcoal)]/70 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
            Results vary with surface type and how long the growth has been there, but the process
            is the same on every job: low-pressure soft washing on siding and roofs, surface
            cleaning on flatwork, and a walk-through with you before we pack up. See{" "}
            <a href="/pricing" className="text-[var(--brand-bright-blue)] font-semibold hover:text-[var(--brand-deep-blue)]">
              typical pricing
            </a>{" "}
            or read{" "}
            <a href="/reviews" className="text-[var(--brand-bright-blue)] font-semibold hover:text-[var(--brand-deep-blue)]">
              customer reviews
            </a>
            .
          </p>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <h2 className={`${H2} text-center mb-8`} style={DISPLAY}>On The Job</h2>
          <LazyMount minHeight={420}>
            <Suspense fallback={<div style={{ minHeight: 420 }} />}>
              <GalleryVideoSlider />
            </Suspense>
          </LazyMount>
        </div>
      </section>

      <InternalLinkRow title="Popular Services" links={POPULAR_SERVICE_LINKS} />
      <InternalLinkRow title="Areas We Serve" links={CORE_CITY_LINKS} tone="light" />

      <InlineQuoteSection />
    </main>
  );
}
